/**
 * Settings → GSTIN & branches route.
 *
 * Loads `companies.get`, hydrates the form with the GSTIN + registration
 * type + place-of-supply mode, and wires the SaveBar to the diff payload
 * + `companies.update`. Mirrors `/settings/company` (same shell, same
 * diff-based save model) — diff scope is just the three fields that ship
 * in V1.0 (gstin, type, defaultPlaceOfSupply).
 *
 * `registeredOn` is shown read-only; edit it on Company profile.
 *
 * @see prototype/settings/settings-gstin.html — visual reference
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { Alert } from "@flint/ui";
import { hasPermission } from "@flint/api-contract";
import { trpc } from "../../../../lib/trpc.ts";
import { formatRelativeTime } from "../../../../lib/format.ts";
import { isTrpcCauseShape } from "../../../../lib/trpc-errors.ts";
import { meQueryOptions } from "../../../../lib/session.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { SettingsShell, SaveBar, type SaveBarState } from "../../../../features/settings/index.ts";
import {
  GstinForm,
  formFromCompany,
  valuesToUpdatePayload,
  isDirtyVs,
  EMPTY_GSTIN_FORM,
  type GstinFormValues,
} from "../../../../features/settings/gstin/index.ts";

export const Route = createFileRoute("/(workspace)/settings/gstin/")({
  // URL-level `settings.manage` gate — see /settings/company/index.tsx for rationale.
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null);
    const perms = me?.company?.permissions ?? [];
    if (!hasPermission(perms, "settings.manage")) {
      throw notFound();
    }
  },
  component: GstinSettingsPage,
});

function GstinSettingsPage() {
  const utils = trpc.useUtils();
  const company = trpc.companies.get.useQuery({});

  const [baseline, setBaseline] = useState<GstinFormValues>(EMPTY_GSTIN_FORM);
  const [values, setValues] = useState<GstinFormValues>(EMPTY_GSTIN_FORM);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof GstinFormValues, string>>>(
    {},
  );

  // Hydrate when the wire data arrives or refetches. Don't clobber the
  // user's in-flight edits on a background refetch.
  useEffect(() => {
    if (!company.data) return;
    const next = formFromCompany(company.data);
    setBaseline(next);
    setValues((current) => (isDirtyVs(current, baseline) ? current : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.data]);

  const update = trpc.companies.update.useMutation({
    onSuccess: async () => {
      await utils.companies.get.invalidate();
      setSavedAt(new Date());
      setFieldErrors({});
    },
  });

  const dirty = isDirtyVs(values, baseline);

  const saveBarState: SaveBarState = update.isPending
    ? "saving"
    : dirty
      ? "dirty"
      : savedAt
        ? "saved"
        : "idle";

  const statusText = useMemo(() => {
    if (update.isPending) return "Saving…";
    if (dirty) return "Unsaved changes — review before saving";
    if (savedAt) return `All changes saved · ${formatRelativeTime(savedAt)}`;
    return "No changes yet";
  }, [update.isPending, dirty, savedAt]);

  const onSave = useCallback(async () => {
    const payload = valuesToUpdatePayload(values, baseline);
    if (!payload) return;

    setFieldErrors({});
    try {
      await update.mutateAsync(payload);
    } catch (err: unknown) {
      // The only locked-field that touches this form is GSTIN. Map the
      // 409 to a field-level error.
      if (isTrpcCauseShape(err, { reason: "locked-field" })) {
        const field = err.data.cause.field;
        if (field === "gstin") {
          setFieldErrors({
            gstin: "GSTIN is locked once set. Contact support to change it.",
          });
        }
      }
    }
  }, [values, baseline, update]);

  const onDiscard = useCallback(() => {
    setValues(baseline);
    setFieldErrors({});
  }, [baseline]);

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Settings" }, { label: "GSTIN & branches" }]}>
      <SettingsShell
        activeSection="gstin"
        pageTitle="GSTIN & branches"
        pageDescription="Your primary GSTIN, registration type, and how Flint picks place-of-supply on every invoice. Multi-state branches, e-Invoice IRN, and e-Way bill land in V1.x."
      >
        {company.isLoading ? (
          <p className="text-muted text-sm">Loading GSTIN settings…</p>
        ) : company.error ? (
          <Alert
            intent="bad"
            title="Couldn't load GSTIN settings"
            description={company.error.message}
          />
        ) : company.data ? (
          <>
            <GstinForm
              values={values}
              baseline={baseline}
              registeredOn={company.data.registeredOn}
              fieldErrors={fieldErrors}
              serverError={
                update.error && Object.keys(fieldErrors).length === 0 ? update.error.message : null
              }
              onChange={setValues}
            />
            <SaveBar
              state={saveBarState}
              statusText={statusText}
              onDiscard={onDiscard}
              onSave={() => void onSave()}
            />
          </>
        ) : null}
      </SettingsShell>
    </WorkspaceShell>
  );
}
